import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import './UserActivityPage.css';

const PAGE_SIZE = 50;
const ACTION = 'ADMIN_PASSWORD_RESET';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

/**
 * Platform-wide audit of admin-initiated password resets. Reads the activity log
 * filtered to ADMIN_PASSWORD_RESET (recorded by the backend on every reset).
 */
export default function PasswordResetAudit() {
  const { t } = useTranslation();
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('');

  const load = useCallback(async (targetPage) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/admin/activity-log', {
        params: { action: ACTION, page: targetPage, size: PAGE_SIZE },
      });
      setLogs(res.data.content || []);
      setTotal(res.data.totalElements || 0);
    } catch {
      setError(t('PasswordResetAudit.loadError'));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(page); }, [page, load]);

  const displayed = filter
    ? logs.filter(l =>
        (l.email   || '').toLowerCase().includes(filter.toLowerCase()) ||
        (l.details || '').toLowerCase().includes(filter.toLowerCase()) ||
        (l.ipAddress || '').includes(filter))
    : logs;

  const exportCSV = () => {
    const rows = [
      [t('PasswordResetAudit.csvTimestamp'), t('PasswordResetAudit.csvPerformedBy'), t('PasswordResetAudit.csvDetails'), t('PasswordResetAudit.csvIp'), t('PasswordResetAudit.csvCity'), t('PasswordResetAudit.csvCountry'), t('PasswordResetAudit.csvStatus')],
      ...logs.map(l => [
        fmtDate(l.createdAt), l.email || l.username, `"${(l.details || '').replace(/"/g, "'")}"`,
        l.ipAddress, l.city, l.country, l.success ? t('PasswordResetAudit.statusOk') : t('PasswordResetAudit.statusFail'),
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `password_resets_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ua-page">
      <div className="ua-selector-bar">
        <span className="ua-table-label">
          <i className="fas fa-key" /> {t('PasswordResetAudit.heading')}
        </span>
        <div className="ua-filter-wrap" style={{ marginLeft: 'auto' }}>
          <i className="fas fa-search ua-filter-icon" />
          <input className="ua-filter-input" placeholder={t('PasswordResetAudit.filterPlaceholder')}
            value={filter} onChange={e => setFilter(e.target.value)} />
          {filter && <button className="ua-filter-clear" onClick={() => setFilter('')}><i className="fas fa-times" /></button>}
        </div>
        <button className="ua-export-btn" onClick={exportCSV} disabled={logs.length === 0} style={{ marginLeft: 12 }}>
          <i className="fas fa-download" /> {t('PasswordResetAudit.exportCsv')}
        </button>
      </div>

      <div className="ua-stat-bar">
        <div className="ua-stat">
          <span className="ua-stat-val">{total}</span>
          <span className="ua-stat-label">{t('PasswordResetAudit.totalResets')}</span>
        </div>
      </div>

      <div className="ua-card">
        {loading && <div className="ua-spinner-wrap"><div className="ua-spinner" /></div>}
        {error   && <div className="ua-error">{error}</div>}

        {!loading && !error && (
          <div className="ua-table-wrap">
            <table className="ua-table">
              <thead>
                <tr>
                  <th>{t('PasswordResetAudit.colTimestamp')}</th>
                  <th>{t('PasswordResetAudit.colPerformedBy')}</th>
                  <th>{t('PasswordResetAudit.colDetails')}</th>
                  <th>{t('PasswordResetAudit.colIpAddress')}</th>
                  <th>{t('PasswordResetAudit.colLocation')}</th>
                  <th>{t('PasswordResetAudit.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={6} className="ua-empty">{t('PasswordResetAudit.emptyState')}</td></tr>
                ) : (
                  displayed.map(l => (
                    <tr key={l.id} className={l.success ? '' : 'ua-row-fail'}>
                      <td className="ua-td-ts">{fmtDate(l.createdAt)}</td>
                      <td>{l.email || l.username || <span className="ua-dim">—</span>}</td>
                      <td>{l.details || <span className="ua-dim">—</span>}</td>
                      <td className="ua-td-ip">{l.ipAddress || <span className="ua-dim">—</span>}</td>
                      <td className="ua-td-loc">
                        {l.city || l.country
                          ? `${l.city || ''}${l.city && l.country ? ', ' : ''}${l.country || ''}`
                          : <span className="ua-dim">—</span>}
                      </td>
                      <td>
                        {l.success
                          ? <span className="ua-badge ua-badge-ok">{t('PasswordResetAudit.statusOk')}</span>
                          : <span className="ua-badge ua-badge-fail">{t('PasswordResetAudit.statusFail')}</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="ua-pagination">
            <button className="ua-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              <i className="fas fa-chevron-left" /> {t('PasswordResetAudit.prev')}
            </button>
            <span className="ua-page-info">{t('PasswordResetAudit.pageInfo', { current: page + 1, total: Math.ceil(total / PAGE_SIZE) })}</span>
            <button className="ua-page-btn" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>
              {t('PasswordResetAudit.next')} <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
